import { Button, ButtonGroup, Input, Separator, Text, useListCollection, VStack } from '@chakra-ui/react';
import {
  SearchFilterFieldName,
  SearchFilterOptionEnum,
} from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { useNotificationSearchContext } from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { useSearchFilterField } from '@components/cockpit/panels/notificationSearch/useSearchFilterField';
import ListboxWithCheckmark from '@components/ui/ListboxWithCheckmark';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveGroupsPlatforms, selectNoFlyZonesNamesAndIds } from '@redux/config/config.selectors';
import { selectAllDetectionSensors } from '@redux/sensors/sensors.selectors';
import { useLazyGetNotificationIdsQuery } from '@services/data/notification.api';
import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationCriteria } from '@/types/data/data.types';

export type SearchItem = {
  label: string;
  value: string;
};

type SearchFieldConfig = {
  minInputLength: number;
  getItems: (input: string, criteria: NotificationCriteria) => Promise<SearchItem[]>;
};

interface SearchTabProps {
  field: SearchFilterFieldName;
  showSuggestionMinInputLength?: number;
}

function getIdSearchCriteria(
  criteria: NotificationCriteria,
  criteriaAddition: Partial<NotificationCriteria>,
): NotificationCriteria {
  return {
    site: criteria.site,
    types: criteria.types,
    notificationStatus: criteria.notificationStatus,
    unpaginated: true,
    ...criteriaAddition,
  };
}

export default function SearchTab({ field, showSuggestionMinInputLength }: Readonly<SearchTabProps>) {
  const platforms = useAppSelector(selectActiveGroupsPlatforms);
  const zonesNamesAndIds = useAppSelector(selectNoFlyZonesNamesAndIds);
  const sensors = useAppSelector(selectAllDetectionSensors);

  const { criteria } = useNotificationSearchContext();

  const { inputValue, setInputValue, selectedItems, syncSelectedItems, selectVisibleItems, deselectVisibleItems } =
    useSearchFilterField(field);

  const hideSuggestions =
    showSuggestionMinInputLength !== undefined && inputValue.length < showSuggestionMinInputLength;

  const { collection: idsCollection, set: setIdsCollection } = useListCollection<SearchItem>({
    initialItems: [],
  });
  const [triggerCallGetNotificationIds, { isLoading: idsIsLoading }] = useLazyGetNotificationIdsQuery();

  const searchFieldConfig: Record<SearchFilterFieldName, SearchFieldConfig> = useMemo(
    () => ({
      [SearchFilterOptionEnum.PLATFORM]: {
        minInputLength: 0,
        getItems: async (input) =>
          platforms
            .filter((platform) => platform.name.toLowerCase().includes(input.toLowerCase()))
            .map((platform) => ({ label: platform.name, value: platform.code })),
      },
      [SearchFilterOptionEnum.COLLABORATIVE_ID]: {
        minInputLength: 3,
        getItems: async (input, criteria) => {
          const result = await triggerCallGetNotificationIds({
            criteria: getIdSearchCriteria(criteria, { displayIds: input ? [input] : [] }),
            displayIds: true,
          });

          return result.data?.map((id) => ({ label: id, value: id })) ?? [];
        },
      },
      [SearchFilterOptionEnum.ID]: {
        minInputLength: 3,
        getItems: async (input, criteria) => {
          const result = await triggerCallGetNotificationIds({
            criteria: getIdSearchCriteria(criteria, { identifiers: input ? [input] : [] }),
            displayIds: false,
          });

          return result.data?.map((id) => ({ label: id, value: id })) ?? [];
        },
      },
      [SearchFilterOptionEnum.ZONES]: {
        minInputLength: 0,
        getItems: async (input) =>
          zonesNamesAndIds
            .filter((nameAndId) => nameAndId.name.toLowerCase().includes(input.toLowerCase()))
            .map((nameAndId) => ({ label: nameAndId.name, value: String(nameAndId.id) })),
      },
      [SearchFilterOptionEnum.TRACK_SENSOR]: {
        minInputLength: 0,
        getItems: async (input) =>
          sensors
            .filter((sensor) => sensor.name.toLowerCase().includes(input.toLowerCase()))
            .map((sensor) => ({
              label: sensor.name,
              value: String(sensor.uniqueKey),
            })),
      },
    }),
    [platforms, sensors, triggerCallGetNotificationIds, zonesNamesAndIds],
  );

  const visibleSelectedCount = idsCollection.items.filter((item) =>
    selectedItems.some((selected) => selected.value === item.value),
  ).length;

  const allVisibleSelected = idsCollection.items.length > 0 && visibleSelectedCount === idsCollection.items.length;

  useEffect(() => {
    if (inputValue.length < searchFieldConfig[field].minInputLength) {
      return;
    }
    searchFieldConfig[field].getItems(inputValue, criteria).then(setIdsCollection);
  }, [criteria, field, inputValue, searchFieldConfig, setIdsCollection]);

  return (
    <VStack backgroundColor="neutral.800" height="100%">
      <VStack padding={3} width="100%" gap={3} alignItems="start">
        {searchFieldConfig[field].minInputLength === 3 && (
          <Text color="neutral.300">
            <FormattedMessage id="components.notificationSearch.filters.searchFilters.size" />
          </Text>
        )}
        <Input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
        <ButtonGroup gap={1}>
          <Button
            variant="toggle"
            size="toggleS"
            disabled={allVisibleSelected}
            onClick={() => selectVisibleItems(idsCollection.items)}
          >
            <FormattedMessage id="global.all" />
          </Button>
          <Button
            variant="toggle"
            size="toggleS"
            disabled={visibleSelectedCount === 0}
            onClick={() => deselectVisibleItems(idsCollection.items)}
          >
            <FormattedMessage id="global.none" />
          </Button>
        </ButtonGroup>
      </VStack>
      <Separator borderColor="neutral.850" />
      {!hideSuggestions && (
        <ListboxWithCheckmark
          collection={idsCollection}
          isLoading={idsIsLoading}
          value={selectedItems.map((item) => item.value)}
          onValueChange={(details) => syncSelectedItems(idsCollection.items, details.value)}
        />
      )}
    </VStack>
  );
}
